import PropTypes from 'prop-types'
import { useCallback, useEffect, useState } from 'react'
import {
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  TextField,
} from '@mui/material'
import { Button } from 'ui/baseComponents'
import ProjectAutocomplete from './ProjectAutocomplete'

const CATEGORIES = [
  { value: 'mobility', label: 'Mobility' },
  { value: 'tremor_dyskinesia', label: 'Tremor & Dyskinesia' },
  { value: 'sleep', label: 'Sleep' },
]

const emptyForm = {
  projectId: '',
  categories: [],
  complianceThresholdDays: 10,
  outreachGapDays: 3,
  maxOutreachAttempts: '',
}

const ConfigurationDialog = ({ open, onClose, onSubmit, config, saving }) => {
  const isEdit = !!config
  const [form, setForm] = useState(emptyForm)

  useEffect(() => {
    if (open) {
      setForm(
        config
          ? {
              projectId: config.projectId,
              categories: config.categories || [],
              complianceThresholdDays: config.complianceThresholdDays,
              outreachGapDays: config.outreachGapDays,
              maxOutreachAttempts:
                config.maxOutreachAttempts != null
                  ? String(config.maxOutreachAttempts)
                  : '',
            }
          : emptyForm,
      )
    }
  }, [open, config])

  const handleCategoryToggle = useCallback((value) => {
    setForm((prev) => ({
      ...prev,
      categories: prev.categories.includes(value)
        ? prev.categories.filter((c) => c !== value)
        : [...prev.categories, value],
    }))
  }, [])

  const canSubmit =
    form.projectId.trim() &&
    form.categories.length > 0 &&
    Number.isInteger(Number(form.complianceThresholdDays)) &&
    Number(form.complianceThresholdDays) > 0 &&
    Number.isInteger(Number(form.outreachGapDays)) &&
    Number(form.outreachGapDays) > 0 &&
    !saving

  const handleSubmit = useCallback(() => {
    if (!canSubmit) return
    onSubmit({
      projectId: form.projectId.trim(),
      categories: form.categories,
      complianceThresholdDays: Number(form.complianceThresholdDays),
      outreachGapDays: Number(form.outreachGapDays),
      maxOutreachAttempts:
        form.maxOutreachAttempts !== ''
          ? Number(form.maxOutreachAttempts)
          : null,
    })
  }, [canSubmit, form, onSubmit])

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isEdit
          ? 'Edit Compliance Configuration'
          : 'Create Compliance Configuration'}
      </DialogTitle>
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          pt: '8px !important',
        }}
      >
        <ProjectAutocomplete
          value={form.projectId}
          onChange={(projectId) => setForm((prev) => ({ ...prev, projectId }))}
          disabled={isEdit}
        />

        <FormControl component="div" required>
          <FormLabel>Categories</FormLabel>
          <FormGroup row>
            {CATEGORIES.map((cat) => (
              <FormControlLabel
                key={cat.value}
                sx={{ ml: 0, mr: 3, gap: 0.5 }}
                control={
                  <Checkbox
                    checked={form.categories.includes(cat.value)}
                    onChange={() => handleCategoryToggle(cat.value)}
                    size="small"
                  />
                }
                label={cat.label}
              />
            ))}
          </FormGroup>
        </FormControl>

        <TextField
          label="Compliance Threshold (days)"
          type="number"
          value={form.complianceThresholdDays}
          onChange={(e) =>
            setForm((prev) => ({
              ...prev,
              complianceThresholdDays: e.target.value,
            }))
          }
          inputProps={{ min: 1 }}
          required
          fullWidth
          size="small"
        />

        <TextField
          label="Outreach Gap (days)"
          type="number"
          value={form.outreachGapDays}
          onChange={(e) =>
            setForm((prev) => ({ ...prev, outreachGapDays: e.target.value }))
          }
          inputProps={{ min: 1 }}
          required
          fullWidth
          size="small"
        />

        <TextField
          label="Max Outreach Attempts (optional)"
          type="number"
          value={form.maxOutreachAttempts}
          onChange={(e) =>
            setForm((prev) => ({
              ...prev,
              maxOutreachAttempts: e.target.value,
            }))
          }
          inputProps={{ min: 1 }}
          fullWidth
          size="small"
          helperText="Leave blank for unlimited attempts"
        />
      </DialogContent>
      <DialogActions>
        <Button type="secondary" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button type="primary" onClick={handleSubmit} disabled={!canSubmit}>
          {saving ? 'Saving...' : isEdit ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

ConfigurationDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  config: PropTypes.shape({
    projectId: PropTypes.string,
    categories: PropTypes.arrayOf(PropTypes.string),
    complianceThresholdDays: PropTypes.number,
    outreachGapDays: PropTypes.number,
    maxOutreachAttempts: PropTypes.number,
  }),
  saving: PropTypes.bool,
}

export default ConfigurationDialog
